/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **The knobs the value line is built from, in one home** (`X38`, `#1229`).
 *
 * They lived inside `MappingControl` because that was the only surface that had them. `X38` is *the
 * expression editor **is** the value line without a depth limit*, so the editor and every branch
 * inside it render the same Default control the row does — and a second copy of it is the defect
 * `X38` exists to remove, not a way to satisfy it.
 *
 * Extracted rather than imported across: `MappingControl` imports the expression editor and the
 * editor imports the value line, so reaching back for a control would close a runtime import cycle
 * for something that is a leaf component in both directions.
 */

import React from 'react';
import { Button, Checkbox, HStack, Input, Text } from '@chakra-ui/react';
import { ControlHover } from '../../ui/ControlHover';
import { FieldHelpTooltip } from '../../ui/FieldHelpTooltip';
import { MAPPING_HELP } from './mapping-help';
import { declaresDefault, expressionDefault, withExpressionDefault } from './mapping-value-round-trip';
import { useExpressionLibrary } from '../../../hooks/useExpressionLibrary';
import { referenceDefaultFrom, withoutReferenceOverride } from './value-line';
import type { MacroExpression, ReferenceExpression } from '../../../types/decorator-expression';
import type { CapabilityRefused } from './MappingControl';

/**
 * **A control's label, its `?`, and nothing else** (#1179, `XS3` slice 3).
 *
 * One shape for every knob on this panel, for the reason `SendAsControl`'s `KNOB_LABEL` already
 * records twice over: *"all four knobs laid out identically"*, reported against one row on two
 * separate days. This is that invariant extended to the `?`, because a help affordance that appears
 * on three of five controls reads as *those three are the confusing ones* rather than as a
 * consistent affordance.
 *
 * The `?` is {@link FieldHelpTooltip}, which is the console's existing one and has twelve call sites
 * on the settings pages and — until this slice — **none** in the mapping editors, which is exactly
 * where the prose accumulated (#1179's own measurement).
 */
export const KnobLabel: React.FC<{ label: string; help: string; muted?: boolean; disabled?: boolean }> = ({
  label,
  help,
  muted = false,
  disabled = false,
}) => (
  <HStack gap={1} align="center" flexShrink={0}>
    <Text
      fontSize="xs"
      fontWeight={muted ? '400' : '600'}
      color={disabled ? 'fg.subtle' : 'fg.muted'}
      whiteSpace="nowrap"
    >
      {label}
    </Text>
    <FieldHelpTooltip ariaLabel={`About ${label}`} content={help} />
  </HStack>
);

/**
 * **A refusal hangs on the control it is about, not beside it** — `X28b` as amended 2026-09-08 and
 * swept console-wide in `#1276`.
 *
 * This was `Refusal`, a `<Text>` rendering the reason as standing prose, on the original `X28b`
 * ruling that *a disabled control whose reason is behind a hover is an absent control again*. That
 * is true of **one** control on **one** screen, and `E2`'s case ladder renders the same row four
 * times at once — eight lines saying the same two things, which is where a statement becomes
 * wallpaper (`#1248`). Ratified after use: *"Help & disabled reason on hover, warnings/errors on the
 * surface feels right."*
 *
 * **What did not change is that a reason never leaves its control.** Todd's question opening slice 3
 * was *"where do we put the disabled reason?"* and the answer is still *with the thing it explains,
 * never collected into a footnote*. Hover is a tighter binding than adjacency, not a looser one.
 *
 * `ControlHover` is the console-wide component, and it holds the measurement — a natively
 * `disabled` control **is** hoverable, checked per control type rather than inherited.
 *
 * (No component lives here any more — the call sites import {@link ControlHover} directly, because a
 * re-export under a second name is one concept with two spellings, which is the drift this file
 * already carries three rules about.)
 */

/**
 * **Default** — what to send when a derived value resolves to nothing (#1128).
 *
 * Rendered **beside the value it qualifies**, on one line, because it is part of the same answer:
 * *read this path, and send this if it is not there.* On its own row it read as a second setting
 * about the row rather than the other half of the mapping (Todd, 2026-08-24).
 *
 * It writes `CalculatedExpression.default`, which is where the model has always carried it —
 * `rowsToParamsDto` writes it, `BaseDriver` resolves the path and then falls through to it, and the
 * ORTB body sends it. The capability was built at four layers out of five and unauthorable at the
 * one the operator touches.
 *
 * **Blank is one of the two answers, not the absence of one** (Todd, 2026-08-21): *send this
 * instead* and *send nothing* are the two things an operator can mean about an unresolved value, and
 * blank is the honest spelling of the behaviour we already have. So an empty box emits `undefined`
 * rather than `''` — the row must collapse back to a plain path, or a mapping the operator never
 * gave a default would start being resolved through the expression engine and stop being omitted.
 *
 * **What blank does is now said on this control, behind its own `?`** (#1179, 2026-09-03). It used
 * to be a sentence appended to the tab's hint under the *value* input — which is why that sentence
 * had to say `Default` by name, and why Todd read it against the on-the-wire options and found the
 * two in conflict. The hover names its own subject by construction, and it **defers** rather than
 * answering: a default prevents the unresolved condition from firing, so what happens without one is
 * the omission control's sentence to say.
 *
 * There is deliberately **no pre-populated value**:
 * `FIELD_CATALOG` declares a `path`, a `kind` and aliases, and no default for any concept. Showing
 * an invented one would teach the operator a default is in force that is not.
 */
export const DefaultValueControl: React.FC<{
  value: string | undefined;
  onChange: (next: string | undefined) => void;
  /** When this surface refuses the capability — rendered disabled, never removed. */
  refused?: CapabilityRefused | null;
  /**
   * **The third answer to this control's own question** (`X33a`, `#1251`) — *let the system's own
   * value stand.* `null` where nothing is beneath this row; see `MappingSurfaceCapabilities`.
   */
  systemDefault?: SystemDefaultAnswer | null;
  /**
   * **A layer this row's default sits on top of** (`X36`, `XS5`, `#1066`) — today, the default
   * declared by the shared expression this row addresses.
   *
   * It renders `X31`'s vocabulary, which `X36` names deliberately rather than reusing the
   * *Accept system default* checkbox: **the inherited value is shown, not implied**, muted and in
   * the box rather than as a placeholder, so *what is in force* is legible without an act. Typing
   * takes the operator's colour and raises `overridden here` with a way back.
   *
   * **It is optional and derived from the value, not declared by the surface** — which is why it
   * escapes the required-and-nullable rule the capabilities follow. A surface does not answer this;
   * `LeafDefaultControl` reads it off the leaf's own shape, in one place, so there is no per-caller
   * answer to forget.
   */
  inheritance?: DefaultInheritance | null;
}> = ({ value, onChange, refused, systemDefault, inheritance }) => {
  const stored = value ?? '';
  const [text, setText] = React.useState(stored);

  // Resync only on an external change — never from our own emit, which drops `''` to
  // `undefined` and would otherwise clear the box a keystroke after it was emptied.
  React.useEffect(() => {
    if ((value ?? '') !== text) setText(value ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // **The input is inert while the system's value stands, and visibly so** (Todd, 2026-09-08). The
  // two are answers to one question, so an enabled box beside a ticked checkbox would be two live
  // controls over one slot — the shape `#1229` is about. Disabling is what keeps the pair readable
  // as *one question, three answers* rather than as two settings.
  const acceptsSystem = accepted(systemDefault);
  const inert = !!refused || acceptsSystem;
  // **What is in force**, which is the operator's question — their own value where they wrote one,
  // and the layer beneath where they did not. Never blank while something stands (`X36`).
  const inherited = inheritance && !inheritance.overridden ? (inheritance.value ?? '') : null;

  /**
   * **Why this box will not take a value, whichever of the two reasons it is.**
   *
   * Reported in the `#1276` rehearsal: with *Accept system default* ticked the box is disabled and
   * the hover said **nothing**, because the sweep read `refused` and a ticked checkbox is not a
   * refusal — the surface offers this control perfectly well, and the operator has switched it off.
   *
   * That is the rule's own words failing on the case they were written for: *a disabled box with no
   * explanation reads as broken, which is worse than absent.* The subject is **why this control is
   * inert**, not *why this surface refuses it*, and reading the narrower of the two is the shape
   * this frame keeps paying for.
   *
   * The second sentence is the way out. A reason with no way back is a statement about the product
   * rather than about what the operator can do — and unlike the refusal, this one has an answer
   * sitting immediately to its right.
   */
  const whyInert = refused
    ? refused.reason
    : acceptsSystem
      ? 'The value beneath this row stands when the mapping resolves to nothing. Untick Accept system default to write your own instead.'
      : null;

  return (
    <HStack gap={2} flexShrink={0}>
      <KnobLabel label="Default" help={MAPPING_HELP.default} muted />
      {/* **The reason travels with the control** — a disabled box with no explanation reads as
          broken, which is worse than absent. It used to say so **twice**: a native `title` here and
          the same sentence as prose below, which is one claim rendered by two mechanisms and is what
          `#1276`'s sweep collapses. The native `title` is what goes: two tooltips on one control is
          the thing an operator sees. */}
      <ControlHover text={whyInert}>
        <Input
          size="sm"
          bg="bg.card"
          fontFamily="mono"
          width="12rem"
          value={refused ? '' : (inherited ?? text)}
          disabled={inert}
          color={inherited !== null ? 'fg.muted' : undefined}
          placeholder={
            refused
              ? 'not available here'
              : acceptsSystem
                ? 'the system value stands'
                : inheritance && inherited === ''
                  ? `${inheritance.from} declares none`
                  : 'if unresolved'
          }
          aria-label="Default value when unresolved"
          onChange={(e) => {
            setText(e.target.value);
            onChange(e.target.value === '' ? undefined : e.target.value);
          }}
        />
      </ControlHover>
      <SystemDefaultControl answer={systemDefault ?? null} />
      <DefaultInheritanceMark inheritance={inheritance ?? null} />
    </HStack>
  );
};

/** Where a row's default comes from when the row does not supply one — see `DefaultValueControl`. */
export type DefaultInheritance = {
  /** What is beneath, named as the operator knows it: the shared expression's name. */
  from: string;
  /** The value that stands when this row supplies none. `undefined` where the layer declares one. */
  value: string | undefined;
  /** Whether this row has written its own over it. */
  overridden: boolean;
  /** Give back the layer's own. `null` where there is nothing to revert to (see `overridden`). */
  onRevert: (() => void) | null;
};

/**
 * **Which layer is answering, and the way back** (`X36`, and `X31`'s vocabulary one level down).
 *
 * `X31` established this for an inherited **row**: an action whose meaning depends on a layer is
 * labelled with its effect on that layer, so *revert* says what it gives back rather than what it
 * removes. A default hanging off a shared expression is the same relationship between two layers,
 * so it reads the same way — which is the point of naming `X31` in `X36` rather than inventing a
 * second vocabulary for one control.
 */
const DefaultInheritanceMark: React.FC<{ inheritance: DefaultInheritance | null }> = ({ inheritance }) => {
  if (!inheritance) return null;
  if (!inheritance.overridden) {
    return (
      <Text fontSize="xs" color="fg.subtle" whiteSpace="nowrap">
        from {inheritance.from}
      </Text>
    );
  }
  return (
    <HStack gap={1} flexShrink={0}>
      <Text fontSize="xs" color="accent.solid" whiteSpace="nowrap">
        overridden here
      </Text>
      {inheritance.onRevert && (
        <Button size="xs" variant="ghost" onClick={inheritance.onRevert}>
          Use {inheritance.from}&apos;s
        </Button>
      )}
    </HStack>
  );
};

/** What a surface says about the value beneath a row — see `MappingSurfaceCapabilities`. */
export type SystemDefaultAnswer =
  | { from: string; value: boolean; onChange: (next: boolean) => void; refused?: CapabilityRefused | null }
  | CapabilityRefused
  | null;

/** Is the system's own value standing for this row? A surface that cannot offer the control still shows it. */
const accepted = (answer: SystemDefaultAnswer | undefined): boolean =>
  !!answer && ('value' in answer ? answer.value : true);

/**
 * **Accept the system default** (`X33a`, `#1251`) — Todd: *"'accept system default' is the concept…
 * maybe a checkbox next to the default field?"*
 *
 * It sits **beside the Default and not in the omission select**, because it answers the Default's
 * question — *what happens when I do not supply a value* — and not the omission control's question
 * of whether the row goes out at all. Two claims on one control is `X46`, and this is the same
 * mistake avoided one control along.
 *
 * **A refusal renders the box ticked and disabled**, never absent. Every surface that has a value
 * beneath a row is *already* falling back to it — that is what the ORTB body has always done — so
 * hiding the control would hide behaviour the operator is subject to, and offering an untick nothing
 * stores is `#1017`'s family. Stating it and saying why it cannot be changed is the honest third
 * thing.
 */
const SystemDefaultControl: React.FC<{ answer: SystemDefaultAnswer }> = ({ answer }) => {
  if (!answer) return null;
  const refused = 'reason' in answer ? answer : (answer.refused ?? null);
  const checked = accepted(answer);
  const onChange = 'value' in answer && !answer.refused ? answer.onChange : undefined;

  return (
    <HStack gap={1} flexShrink={0}>
      <ControlHover text={refused?.reason ?? null}>
        <Checkbox.Root
          size="sm"
          checked={checked}
          disabled={!onChange}
          onCheckedChange={(e) => onChange?.(e.checked === true)}
        >
          <Checkbox.HiddenInput aria-label="Accept the system default when this resolves to nothing" />
          <Checkbox.Control />
          <Checkbox.Label fontSize="xs" color="fg.muted" whiteSpace="nowrap">
            Accept system default
          </Checkbox.Label>
        </Checkbox.Root>
      </ControlHover>
      {/* The value, not the mechanism — an operator needs to know *what* stands, not which code
          path supplies it (`#1045`). Absent on a refusal, whose own sentence is the thing to read. */}
      {!refused && 'from' in answer && (
        <Text fontSize="xs" color="fg.subtle" fontFamily="mono" whiteSpace="nowrap">
          {answer.from}
        </Text>
      )}
    </HStack>
  );
};

/**
 * **The Default for an expression, wherever that expression is being edited** (`X38`).
 *
 * *Which shapes declare a default* is one rule and it was written twice — `LeafDefaultControl`'s
 * `calculated || computed` on the row, and `CalculatedExpressionForm`'s and `ComputedExpressionForm`'s
 * own Default inputs inside the editor. The two agreed, which is what let the editor draw a second
 * box over the row's for a fortnight: the value was one slot, the controls were two, and whichever
 * one an operator typed in the other re-rendered from.
 *
 * A **conditional** declares none, and correctly: both branches always produce something, or one of
 * them suppresses. So it renders nothing rather than a disabled box about a question that does not
 * arise — the `null` answer, not the refusal (pattern 24 distinguishes them).
 */
export const ReferenceDefaultControl: React.FC<{
  expression: ReferenceExpression;
  onChange: (next: MacroExpression) => void;
}> = ({ expression, onChange }) => {
  const { data, isLoading, isError } = useExpressionLibrary();
  const entry = (data ?? []).find((candidate) => candidate.id === expression.ref);
  const target = entry?.expression;
  const targetDeclares = target ? declaresDefault(target) : undefined;

  /**
   * **Three outcomes and they are not one** — the same rule the read-out follows. Until the library
   * answers, what is beneath is *unknown*, and a control that said `declares none` while the request
   * was in flight would invite an operator to write an override over a default that exists.
   */
  if (isLoading || isError || !entry) {
    return (
      <DefaultValueControl
        value={expression.default}
        onChange={() => undefined}
        refused={{
          reason: isLoading
            ? 'Reading the shared expression this row uses, so what it falls back to is not known yet.'
            : isError
              ? 'The library could not be read, so nothing is claimed about what this falls back to.'
              : 'This row addresses an expression that is no longer in the library, so there is nothing to fall back to.',
        }}
      />
    );
  }

  /**
   * **A shape with no default slot has nothing to override**, and the control says so rather than
   * storing a value the resolver then ignores. `X33a`'s third answer, one layer in: the question
   * does not arise here, and that is different from the feature being missing.
   */
  if (!targetDeclares) {
    return (
      <DefaultValueControl
        value={undefined}
        onChange={() => undefined}
        refused={{
          reason: `${entry.name} always produces a value, so there is nothing here to fall back to.`,
        }}
      />
    );
  }

  return (
    <DefaultValueControl
      value={expression.default ?? expressionDefault(targetDeclares)}
      onChange={(next) => onChange(referenceDefaultFrom(expression, next))}
      inheritance={{
        from: entry.name,
        value: expressionDefault(targetDeclares),
        overridden: expression.default !== undefined,
        // **Revert removes the override rather than writing the inherited value back**, which is the
        // whole of `X31`: giving back the layer's own means the row stops answering, so a later
        // change in the library reaches it again. Writing the value in would look identical and
        // freeze it.
        onRevert: () => onChange(withoutReferenceOverride(expression)),
      }}
    />
  );
};

export const ExpressionDefaultControl: React.FC<{
  expression: MacroExpression;
  onChange: (next: MacroExpression) => void;
  /** `X33a` — passed through, so the pair stays one question wherever the Default renders. */
  systemDefault?: SystemDefaultAnswer | null;
}> = ({ expression, onChange, systemDefault }) => {
  // The condition itself lives in `declaresDefault`, because the **reader** of this same rule
  // (`leafDefault`, for the row's `⤵` mark) had its own narrower copy and disagreed — `#1269`.
  const declares = declaresDefault(expression);
  if (!declares) return null;
  return (
    <DefaultValueControl
      value={expressionDefault(declares)}
      onChange={(next) => onChange(withExpressionDefault(declares, next))}
      systemDefault={systemDefault}
    />
  );
};
